import { useCallback, useEffect, useRef, useState } from 'react';
import { formatPrice } from '../data/products';
import { useAdminSession } from '../data/useAdminSession';
import { useCatalog } from '../data/useCatalog';
import { useShipping } from '../data/useShipping';
import { usePayment } from '../data/usePayment';
import { TRANSFER_METHODS, waLink } from '../data/store';
import LoginForm from '../components/LoginForm/LoginForm';
import './Admin.css';

const EMPTY_PAYMENT = { whatsapp: '', vodafone: '', instapay: '' };

const EMPTY_PRODUCT = {
  name: '',
  category: 'tshirts',
  price: '',
  oldPrice: '',
  sizes: '',
  primaryColor: '',
  primaryColorName: '',
  tag: '',
};

const EMPTY_REVIEW = { reviewName: '', city: '', text: '' };

const NEW_CATEGORY = '__new';

const GARMENT_COLORS = [
  { id: 'black', label: 'Black', value: '#171717' },
  { id: 'white', label: 'White', value: '#ffffff' },
  { id: 'red', label: 'Red', value: '#dc2626' },
  { id: 'blue', label: 'Blue', value: '#2563eb' },
  { id: 'navy', label: 'Navy', value: '#1e3a8a' },
  { id: 'gray', label: 'Gray', value: '#8b909a' },
  { id: 'green', label: 'Green', value: '#16a34a' },
  { id: 'yellow', label: 'Yellow', value: '#facc15' },
  { id: 'brown', label: 'Brown', value: '#8b5e3c' },
  { id: 'beige', label: 'Beige', value: '#d6c2a1' },
  { id: 'pink', label: 'Pink', value: '#ec4899' },
  { id: 'olive', label: 'Olive', value: '#5a5f2e' },
  { id: 'charcoal', label: 'Charcoal', value: '#36393f' },
  { id: 'bone', label: 'Bone', value: '#e8e2d4' },
  { id: 'sand', label: 'Sand', value: '#cbb894' },
];

function ColorChoice({ value, onChange, required = false, disabled = false, ariaLabel, placeholder, className = 'admin__input' }) {
  const custom = /^#[\da-f]{6}$/i.test(value) && !GARMENT_COLORS.some((item) => item.id === value);
  const swatch = custom ? value : GARMENT_COLORS.find((item) => item.id === value)?.value ?? '#dc2626';
  return (
    <div className="admin-color-choice">
      <select className={className} aria-label={ariaLabel} value={custom ? '' : value} onChange={(event) => onChange(event.target.value)} required={required && !custom} disabled={disabled}>
        <option value="">{placeholder}</option>
        {GARMENT_COLORS.map((color) => <option value={color.id} key={color.id}>{color.label}</option>)}
      </select>
      <label className="admin-color-choice__custom" title="Choose a custom color" aria-label="Choose a custom color">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3a9 9 0 1 0 0 18h1.2a2 2 0 0 0 1.5-3.3 1.4 1.4 0 0 1 1-2.4H18a3 3 0 0 0 3-3c0-5.1-4-9.3-9-9.3Z"/><circle cx="7.5" cy="10" r="1"/><circle cx="11" cy="6.8" r="1"/><circle cx="15.5" cy="8" r="1"/></svg>
        <input type="color" value={swatch} onChange={(event) => onChange(event.target.value)} disabled={disabled} />
      </label>
      {custom && <span className="admin-color-choice__value" style={{ backgroundColor: value }} title={value} />}
    </div>
  );
}

const SECTIONS = [
  { id: 'orders', label: 'Orders' },
  { id: 'history', label: 'Order History' },
  { id: 'products', label: 'Products' },
  { id: 'add-product', label: 'Add Product' },
  { id: 'reviews', label: 'Reviews' },
  { id: 'add-review', label: 'Add Review' },
  { id: 'shipping', label: 'Shipping' },
  { id: 'payment', label: 'Payment' },
];

const categoryTitle = (categories, id) => categories.find((category) => category.id === id)?.title ?? id;

const toSizeList = (value) =>
  Array.isArray(value) ? value : String(value).split(',');

const toShippingRows = (fees) =>
  Object.entries(fees ?? {}).map(([name, fee], index) => ({ key: `shipping-${index}-${name}`, name, fee: String(fee) }));

const APPAREL_SIZES = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

function ApparelSizePicker({ value, onChange }) {
  const [customSize, setCustomSize] = useState('');
  const selected = toSizeList(value).map((size) => String(size).trim()).filter(Boolean);

  function toggle(size) {
    onChange(selected.includes(size) ? selected.filter((item) => item !== size).join(', ') : [...selected, size].join(', '));
  }

  function addCustom(event) {
    event.preventDefault();
    const size = customSize.trim();
    if (!size || selected.includes(size)) return;
    onChange([...selected, size].join(', '));
    setCustomSize('');
  }

  return (
    <div className="size-picker" role="group" aria-labelledby="sizes-label">
      <div className="size-picker__options">
        {APPAREL_SIZES.map((size) => (
          <button
            type="button"
            key={size}
            className={`size-picker__size${selected.includes(size) ? ' is-selected' : ''}`}
            aria-pressed={selected.includes(size)}
            onClick={() => toggle(size)}
          >
            {size}
          </button>
        ))}
      </div>
      <div className="size-picker__custom">
        <input
          className="admin__input"
          type="number"
          min="1"
          step="any"
          value={customSize}
          onChange={(event) => setCustomSize(event.target.value)}
          onKeyDown={(event) => { if (event.key === 'Enter') addCustom(event); }}
          placeholder="Other size"
          aria-label="Enter another size"
        />
        <button type="button" className="size-picker__add" onClick={addCustom} disabled={!customSize.trim()} aria-label="Add custom size">
          +
        </button>
      </div>
    </div>
  );
}

// The server already explains what it rejected; only a dead connection needs invented words.
async function serverError(response, fallback) {
  const data = await response.json().catch(() => ({}));
  return data.error || fallback;
}

function describeFailure(error, offlineMessage) {
  return error instanceof TypeError ? offlineMessage : error.message || offlineMessage;
}

const formatWhen = (iso) =>
  iso
    ? new Date(iso).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Unknown date';

function Field({ id, label, optional, full, children }) {
  return (
    <div className={`admin-field${full ? ' admin-field--full' : ''}`}>
      <label className="admin-field__label" id={`${id}-label`} htmlFor={id}>
        {label}
        {optional && <span className="admin-field__optional">{optional}</span>}
      </label>
      {children}
    </div>
  );
}

function ProductVariantEditor({ variants, onChange, idPrefix }) {
  function update(index, field, value) {
    onChange(variants.map((variant, itemIndex) => itemIndex === index ? { ...variant, [field]: value } : variant));
  }

  function selectImage(index, file) {
    if (!file) return;
    const previous = variants[index].preview;
    if (previous?.startsWith('blob:')) URL.revokeObjectURL(previous);
    const preview = URL.createObjectURL(file);
    onChange(variants.map((variant, itemIndex) => itemIndex === index ? { ...variant, file, preview } : variant));
  }

  function remove(index) {
    const preview = variants[index].preview;
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    onChange(variants.filter((_, itemIndex) => itemIndex !== index));
  }

  return (
    <div className="product-variant-editor">
      {variants.map((variant, index) => (
        <div className="product-variant-editor__row" key={variant.key ?? `${idPrefix}-${index}`}>
          <ColorChoice ariaLabel={`Variant ${index + 1} color`} value={variant.color} onChange={(value) => update(index, 'color', value)} required placeholder="Choose color" />
          <input
            className="admin__input"
            aria-label={`Variant ${index + 1} color name`}
            value={variant.name}
            onChange={(event) => update(index, 'name', event.target.value)}
            placeholder="Color name, e.g. Triple Black"
          />
              <label className="product-variant-editor__upload">
            <span>{variant.file?.name || (variant.image ? 'Replace image' : 'Choose image')}</span>
            <input
              type="file"
              accept="image/*"
              aria-label={`Image for ${variant.name || `variant ${index + 1}`}`}
              onChange={(event) => selectImage(index, event.target.files?.[0])}
            />
          </label>
          {(variant.preview || variant.image) && (
            <img className="product-variant-editor__preview" src={variant.preview || variant.image} alt="" />
          )}
          <button type="button" className="product-variant-editor__remove" onClick={() => remove(index)}>
            Remove
          </button>
        </div>
      ))}
      <button
        type="button"
        className="product-variant-editor__add"
        onClick={() => onChange([
          ...variants,
          { key: `${idPrefix}-${Date.now()}-${Math.random()}`, color: '', name: '', image: '', file: null, preview: '' },
        ])}
      >
        Add another color
      </button>
    </div>
  );
}

function GalleryEditor({ photos, onChange, idPrefix }) {
  function add(files) {
    const picked = [...files].filter((file) => file.type.startsWith('image/'));
    if (!picked.length) return;
    onChange([
      ...photos,
      ...picked.map((file) => ({
        key: `${idPrefix}-${Date.now()}-${Math.random()}`,
        image: '',
        file,
        preview: URL.createObjectURL(file),
      })),
    ]);
  }

  function remove(index) {
    const preview = photos[index].preview;
    if (preview?.startsWith('blob:')) URL.revokeObjectURL(preview);
    onChange(photos.filter((_, itemIndex) => itemIndex !== index));
  }

  return (
    <div className="product-gallery">
      {photos.map((shot, index) => (
        <div className="product-gallery__shot" key={shot.key ?? `${idPrefix}-${index}`}>
          <img src={shot.preview || shot.image} alt="" />
          <button type="button" className="product-gallery__remove" aria-label="Remove this detail photo" onClick={() => remove(index)}>
            &times;
          </button>
        </div>
      ))}
      <label className="product-gallery__add">
        <span>Add photos</span>
        <input
          type="file"
          accept="image/*"
          multiple
          aria-label="Detail photos"
          onChange={(event) => {
            add(event.target.files);
            event.target.value = '';
          }}
        />
      </label>
    </div>
  );
}

// The frame loads this page instead of the image itself: the browser's own image viewer leaves a
// tall receipt scrollable, while this scales it down to fit and centres it.
// `url` is always a createObjectURL blob, never buyer text.
function shotPage(url) {
  return `<!doctype html><meta charset="utf-8"><style>
    html,body{height:100%;margin:0;overflow:hidden;background:#0b0d10}
    body{display:grid;place-items:center}
    img{max-width:96vw;max-height:96vh;object-fit:contain;display:block}
  </style><img src="${url}" alt="Transfer screenshot">`;
}

function OrderCard({ order, onReview, reviewBusy, onDelete, deleteBusy }) {
  const { customer = {}, items = [] } = order;
  const chatLink = waLink(customer.whatsapp || customer.phone);
  const { authFetch } = useAdminSession();
  const [shot, setShot] = useState('');
  const [shotError, setShotError] = useState('');

  useEffect(() => {
    if (!shot) return undefined;
    function handleKeyDown(event) {
      if (event.key === 'Escape') setShot('');
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      URL.revokeObjectURL(shot);
    };
  }, [shot]);

  // The screenshot is pulled as a blob: a plain link to /api/... is a page navigation, and the
  // dev-server proxy hands page navigations back as index.html instead of forwarding them.
  async function openShot() {
    setShotError('');
    try {
      const response = await authFetch(`/api/receipts/${encodeURIComponent(order.receiptFile)}`);
      if (!response.ok) throw new Error('missing');
      setShot(URL.createObjectURL(await response.blob()));
    } catch (error) {
      setShotError('The screenshot could not be opened.');
    }
  }

  return (
    <article className="order">
      <header className="order__head">
        <h3 className="order__id">{order.id}</h3>
        <div className="order__head-actions">
          <p className="order__when">{formatWhen(order.receivedAt)}</p>
          <button
            type="button"
            className={`order__review${order.reviewed ? ' order__review--done' : ''}`}
            aria-pressed={Boolean(order.reviewed)}
            disabled={reviewBusy}
            onClick={() => onReview(order)}
          >
            {reviewBusy ? 'Saving…' : order.reviewed ? 'Reviewed' : 'Review'}
          </button>
          <button
            type="button"
            className="order__delete"
            disabled={deleteBusy || reviewBusy}
            onClick={() => onDelete(order)}
          >
            {deleteBusy ? 'Deleting…' : 'Delete order'}
          </button>
        </div>
      </header>

      <div className="order__grid">
        <section className="order__block">
          <h4 className="order__label">Buyer</h4>
          <p className="order__line order__line--strong">{customer.fullName || 'No name'}</p>
          {customer.phone && <p className="order__line">{customer.phone}</p>}
          {customer.whatsapp && <p className="order__line">WhatsApp {customer.whatsapp}</p>}
          {chatLink && (
            <a
              className="order__wa"
              href={chatLink}
              target="_blank"
              rel="noreferrer"
              aria-label={`Chat with ${customer.fullName || 'this buyer'} on WhatsApp`}
            >
              <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
                <path
                  d="M20.5 3.5A11.8 11.8 0 0 0 12.1 0C5.5 0 .1 5.4.1 12c0 2.1.6 4.2 1.6 6L0 24l6.2-1.6a12 12 0 0 0 5.9 1.5h.1c6.6 0 12-5.4 12-12a11.9 11.9 0 0 0-3.7-8.4ZM12.2 21.8a10 10 0 0 1-5.1-1.4l-.4-.2-3.7 1 1-3.6-.2-.4A9.8 9.8 0 0 1 2.3 12a9.9 9.9 0 0 1 16.9-7 9.8 9.8 0 0 1 2.9 7 9.9 9.9 0 0 1-9.9 9.8Zm5.4-7.4c-.3-.1-1.7-.8-2-.9-.3-.1-.5-.1-.7.2-.2.3-.7.9-.9 1.1-.2.2-.3.2-.6.1-.3-.1-1.2-.4-2.3-1.4-.8-.7-1.4-1.6-1.5-1.9-.2-.3 0-.4.1-.6l.4-.5c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.7-1.6-.9-2.1-.2-.5-.5-.4-.7-.4h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.4s1 2.8 1.1 3c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.1-1.4-.1-.1-.3-.2-.6-.3Z"
                  fill="currentColor"
                />
              </svg>
              Customer WhatsApp
            </a>
          )}
        </section>

        <section className="order__block">
          <h4 className="order__label">Deliver to</h4>
          <p className="order__line order__line--strong">{customer.governorate || 'No governorate'}</p>
          {customer.city && <p className="order__line">{customer.city}</p>}
          {customer.address && <p className="order__line">{customer.address}</p>}
          {customer.notes && <p className="order__line order__line--note">Note: {customer.notes}</p>}
        </section>

        <section className="order__block">
          <h4 className="order__label">Items</h4>
          <ul className="order__items">
            {items.map((item) => (
              <li className="order__item" key={`${item.id}-${item.color ?? 'base'}-${item.qty}`}>
                <span className="order__qty">{item.qty}&times;</span>
                <span className="order__item-name">
                  {item.name}
                  {item.colorName ? ` · ${item.colorName}` : ''}
                  {item.size ? ` · Size ${item.size}` : ''}
                </span>
                <span className="order__item-total">{formatPrice(item.lineTotal ?? item.price * item.qty)}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="order__block">
          <h4 className="order__label">Shipping deposit</h4>
          <p className="order__line order__line--strong">{formatPrice(order.shippingFee ?? 0)}</p>
          {order.transferMethod?.label && (
            <p className="order__line">
              {order.transferMethod.label} · <span className="order__line--strong">{order.transferMethod.number}</span>
            </p>
          )}
          <p className="order__line">
            Transferred from <span className="order__line--strong">{order.transferFrom || 'not given'}</span>
          </p>
          {order.receiptFile ? (
            <>
              <button type="button" className="order__receipt" onClick={openShot}>
                Open transfer screenshot
              </button>
              {shotError && <p className="order__line order__line--note">{shotError}</p>}
            </>
          ) : (
            <p className="order__line order__line--note">No screenshot uploaded</p>
          )}
        </section>
      </div>

      <footer className="order__money">
        <span>Subtotal {formatPrice(order.subtotal ?? 0)}</span>
        <span>Shipping {formatPrice(order.shippingFee ?? 0)}</span>
        <span className="order__cod">Collect at door {formatPrice(order.subtotal ?? 0)}</span>
        <span className="order__total">Order total {formatPrice(order.total ?? 0)}</span>
      </footer>

      {shot && (
        <div
          className="shot"
          role="dialog"
          aria-modal="true"
          aria-label={`Transfer screenshot for ${order.id}`}
          onClick={() => setShot('')}
        >
          <iframe
            className="shot__frame"
            srcDoc={shotPage(shot)}
            title={`Transfer screenshot for order ${order.id}`}
            onClick={(event) => event.stopPropagation()}
          />
          <button type="button" className="shot__close" onClick={() => setShot('')}>
            Close
          </button>
        </div>
      )}
    </article>
  );
}

const toGalleryDraft = (gallery) => (gallery ?? []).map((image, index) => ({ key: `saved-gallery-${index}`, image, file: null, preview: '' }));

function ProductRow({ product, categories, onDone, onFail }) {
  const { authFetch } = useAdminSession();
  const [outOfStock, setOutOfStock] = useState(Boolean(product.outOfStock));
  const [draft, setDraft] = useState({
    name: product.name,
    price: String(product.price),
    sizes: (product.sizes ?? []).join(', '),
    primaryColor: product.primaryColor ?? '',
    primaryColorName: product.primaryColorName ?? '',
    colorVariants: (product.colorVariants ?? []).map((variant, index) => ({ ...variant, key: `saved-${index}`, file: null, preview: '' })),
    gallery: toGalleryDraft(product.gallery),
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => setOutOfStock(Boolean(product.outOfStock)), [product.outOfStock]);
  useEffect(() => {
    setDraft({
      name: product.name,
      price: String(product.price),
      sizes: (product.sizes ?? []).join(', '),
      primaryColor: product.primaryColor ?? '',
      primaryColorName: product.primaryColorName ?? '',
      colorVariants: (product.colorVariants ?? []).map((variant, index) => ({ ...variant, key: `saved-${index}`, file: null, preview: '' })),
      gallery: toGalleryDraft(product.gallery),
    });
  }, [product.name, product.price, product.sizes, product.colors, product.primaryColor, product.primaryColorName, product.colorVariants, product.gallery]);

  const editedSizes = toSizeList(draft.sizes).map((size) => String(size).trim()).filter(Boolean).join(',');
  const cleanName = draft.name.trim();
  const dirty =
    cleanName !== product.name ||
    Number(draft.price) !== product.price ||
    editedSizes !== (product.sizes ?? []).join(',') ||
    draft.primaryColor !== (product.primaryColor ?? '') ||
    draft.primaryColorName !== (product.primaryColorName ?? '') ||
    JSON.stringify(draft.colorVariants.map(({ color, name, image, file }) => ({ color, name, image, file: file?.name ?? '' }))) !==
      JSON.stringify((product.colorVariants ?? []).map(({ color, name, image }) => ({ color, name, image, file: '' }))) ||
    draft.gallery.map(({ image, file }) => image || file.name).join('|') !== (product.gallery ?? []).join('|');

  function edit(field) {
    return (event) => setDraft((current) => ({ ...current, [field]: event.target.value }));
  }

  async function save() {
    setBusy(true);
    try {
      const supportsColors = true;
      if (supportsColors && Boolean(draft.primaryColor) !== Boolean(draft.primaryColorName.trim())) {
        onFail('Choose the main image color and enter its name, or leave both blank.');
        return;
      }
      if (supportsColors && draft.colorVariants.some((variant) =>
        !variant.color || !variant.name.trim() || (!variant.file && !variant.image)
      )) {
        onFail('Complete each extra color with its color, name and photo.');
        return;
      }
      const body = new FormData();
      body.append('product', JSON.stringify({
        name: cleanName,
        price: Number(draft.price),
        sizes: toSizeList(draft.sizes),
        ...(supportsColors ? {
          primaryColor: draft.primaryColor,
          primaryColorName: draft.primaryColorName,
          colorVariants: draft.colorVariants.map(({ color, name, image }, imageIndex) => ({ color, name, image, imageIndex })),
        } : {}),
        gallery: draft.gallery.map(({ image }, imageIndex) => ({ image, imageIndex })),
      }));
      draft.colorVariants.forEach((variant, index) => {
        if (variant.file) body.append(`variantImage-${index}`, variant.file, variant.file.name);
      });
      draft.gallery.forEach((shot, index) => {
        if (shot.file) body.append(`galleryImage-${index}`, shot.file, shot.file.name);
      });
      const response = await authFetch(`/api/products/${product.id}`, {
        method: 'PATCH',
        body,
      });
      if (!response.ok) throw new Error(await serverError(response, 'The server refused that change.'));
      draft.colorVariants.forEach((variant) => {
        if (variant.preview?.startsWith('blob:')) URL.revokeObjectURL(variant.preview);
      });
      draft.gallery.forEach((shot) => {
        if (shot.preview?.startsWith('blob:')) URL.revokeObjectURL(shot.preview);
      });
      onDone(`"${cleanName}" updated on the store.`);
    } catch (error) {
      onFail(describeFailure(error, 'Could not save that change. Run `npm run server` and try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Remove "${product.name}" from the store?`)) return;

    setBusy(true);
    try {
      const response = await authFetch(`/api/products/${product.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await serverError(response, 'The server refused the delete.'));
      onDone(`"${product.name}" is off the store now.`);
    } catch (error) {
      onFail(describeFailure(error, 'Could not delete that product. Run `npm run server` and try again.'));
      setBusy(false);
    }
  }

  async function toggleStock() {
    setBusy(true);
    const nextOutOfStock = !outOfStock;
    try {
      const response = await authFetch(`/api/products/${product.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outOfStock: nextOutOfStock }),
      });
      if (!response.ok) throw new Error(await serverError(response, 'The server refused the stock update.'));
      setOutOfStock(nextOutOfStock);
      onDone(nextOutOfStock ? `"${product.name}" is marked out of stock.` : `"${product.name}" is back in stock.`);
    } catch (error) {
      onFail(describeFailure(error, 'Could not update stock status. Run `npm run server` and try again.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="catalog__row">
      <span className="catalog__thumb">{product.image && <img src={product.image} alt="" />}</span>
      <input
        className="catalog__input catalog__input--name"
        value={draft.name}
        onChange={edit('name')}
        aria-label={`Name of ${product.name}`}
      />
      <span className="catalog__meta">
        {categoryTitle(categories, product.category)}
      </span>
      <input
        className="catalog__input catalog__input--price"
        type="number"
        min="1"
        value={draft.price}
        onChange={edit('price')}
        aria-label={`Price of ${product.name} in EGP`}
      />
      <input
        className="catalog__input catalog__input--sizes"
        value={draft.sizes}
        onChange={edit('sizes')}
        aria-label={`Sizes of ${product.name}, comma separated`}
      />
      <span className="catalog__actions">
        <button
          type="button"
          className={`catalog__stock${outOfStock ? ' catalog__stock--out' : ''}`}
          disabled={busy}
          onClick={toggleStock}
        >
          {outOfStock ? 'Restore stock' : 'Mark out of stock'}
        </button>
        <button type="button" className="catalog__save" disabled={!dirty || busy} onClick={save}>
          {busy ? 'Saving' : 'Save'}
        </button>
        <button type="button" className="catalog__delete" disabled={busy} onClick={remove}>
          Delete
        </button>
      </span>
      {(
        <details className="catalog__colors">
          <summary>Main color and other color photos</summary>
          <div className="catalog__primary-color">
            <ColorChoice className="catalog__input" ariaLabel={`Main photo color of ${product.name}`} value={draft.primaryColor} onChange={(value) => setDraft((current) => ({ ...current, primaryColor: value }))} disabled={!product.image} placeholder="Choose main photo color" />
            <input
              className="catalog__input"
              aria-label={`Main photo color name of ${product.name}`}
              value={draft.primaryColorName}
              onChange={edit('primaryColorName')}
              placeholder="Main photo name, e.g. Triple Black"
              disabled={!product.image}
            />
          </div>
          <ProductVariantEditor
            idPrefix={`product-${product.id}-variant`}
            variants={draft.colorVariants}
            onChange={(colorVariants) => setDraft((current) => ({ ...current, colorVariants }))}
          />
          <div className="catalog__gallery">
            <span className="admin-field__label">Detail photos</span>
            <GalleryEditor
              idPrefix={`product-${product.id}-gallery`}
              photos={draft.gallery}
              onChange={(gallery) => setDraft((current) => ({ ...current, gallery }))}
            />
          </div>
        </details>
      )}
    </li>
  );
}

function Admin() {
  const [section, setSection] = useState('orders');
  const [orders, setOrders] = useState(null);
  const [ordersError, setOrdersError] = useState('');
  const [reviewingOrderId, setReviewingOrderId] = useState('');
  const [deletingOrderId, setDeletingOrderId] = useState('');
  const [productForm, setProductForm] = useState(EMPTY_PRODUCT);
  const [productVariantDrafts, setProductVariantDrafts] = useState([]);
  const [productGalleryDrafts, setProductGalleryDrafts] = useState([]);
  const [productMessage, setProductMessage] = useState('');
  const [productError, setProductError] = useState('');
  const [productBusy, setProductBusy] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [photo, setPhoto] = useState(null);
  const [reviewForm, setReviewForm] = useState(EMPTY_REVIEW);
  const [reviewMessage, setReviewMessage] = useState('');
  const [reviewError, setReviewError] = useState('');
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewDeletingId, setReviewDeletingId] = useState('');
  const [shippingRows, setShippingRows] = useState([]);
  const [shippingMessage, setShippingMessage] = useState('');
  const [shippingError, setShippingError] = useState('');
  const [shippingBusy, setShippingBusy] = useState(false);
  const [paymentForm, setPaymentForm] = useState(EMPTY_PAYMENT);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [paymentError, setPaymentError] = useState('');
  const [paymentBusy, setPaymentBusy] = useState(false);
  const { fees: shippingFees } = useShipping();
  const { payment, reload: reloadPayment } = usePayment();
  const { products, reviews, categories, reload, load } = useCatalog();
  const { authenticated, status: sessionStatus, username: adminName, authFetch, logout } = useAdminSession();
  const photoInput = useRef(null);

  const loadOrders = useCallback(() => {
    authFetch('/api/orders')
      .then(async (response) => {
        if (!response.ok) throw new Error(await serverError(response, `The orders server answered ${response.status}.`));
        return response.json();
      })
      .then((data) => {
        setOrders(data.orders ?? []);
        setOrdersError('');
      })
      .catch((error) => {
        setOrders([]);
        setOrdersError(describeFailure(error, 'Could not reach the orders server. Run `npm run server`, then retry.'));
      });
  }, [authFetch]);

  async function toggleOrderReview(order) {
    setReviewingOrderId(order.id);
    setOrdersError('');
    try {
      const response = await authFetch(`/api/orders/${encodeURIComponent(order.id)}/review`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewed: !order.reviewed }),
      });
      if (!response.ok) throw new Error(await serverError(response, 'The server could not update this review status.'));
      const data = await response.json();
      setOrders((current) => current?.map((item) => item.id === order.id ? { ...item, reviewed: data.reviewed } : item) ?? current);
    } catch (error) {
      setOrdersError(describeFailure(error, 'Could not update the order review status. Run `npm run server`, then retry.'));
    } finally {
      setReviewingOrderId('');
    }
  }

  async function deleteOrder(order) {
    if (!window.confirm(`Delete order ${order.id}? This cannot be undone.`)) return;
    setDeletingOrderId(order.id);
    setOrdersError('');
    try {
      const response = await authFetch(`/api/orders/${encodeURIComponent(order.id)}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await serverError(response, 'The server could not delete this order.'));
      setOrders((current) => current?.filter((item) => item.id !== order.id) ?? current);
    } catch (error) {
      setOrdersError(describeFailure(error, 'Could not delete the order. Run `npm run server`, then retry.'));
    } finally {
      setDeletingOrderId('');
    }
  }

  const allOrders = orders ?? [];
  const pendingOrders = allOrders.filter((order) => !order.reviewed);
  const reviewedOrders = allOrders.filter((order) => order.reviewed);
  const filteredProducts = products.filter((product) => {
    const query = productSearch.trim().toLocaleLowerCase();
    if (!query) return true;
    return [product.name, product.id, categoryTitle(categories, product.category)]
      .filter(Boolean)
      .some((value) => String(value).toLocaleLowerCase().includes(query));
  });

  useEffect(() => {
    if (authenticated) loadOrders();
  }, [authenticated, loadOrders]);

  useEffect(() => {
    setShippingRows(toShippingRows(shippingFees));
  }, [shippingFees]);

  useEffect(() => {
    setPaymentForm(payment);
  }, [payment]);

  function refreshAll() {
    loadOrders();
    reload();
  }

  function editProduct(event) {
    const { id, value } = event.target;
    setProductForm((form) => ({
      ...form,
      [id]: value,
    }));
  }

  function editReview(event) {
    setReviewForm((form) => ({ ...form, [event.target.id]: event.target.value }));
  }

  function editShippingRow(index, field, value) {
    setShippingMessage('');
    setShippingError('');
    setShippingRows((rows) => rows.map((row, position) => (position === index ? { ...row, [field]: value } : row)));
  }

  function addShippingRow() {
    setShippingMessage('');
    setShippingRows((rows) => [...rows, { key: `shipping-${Date.now()}`, name: '', fee: '' }]);
  }

  function removeShippingRow(index) {
    setShippingMessage('');
    setShippingError('');
    setShippingRows((rows) => rows.filter((_, position) => position !== index));
  }

  async function submitShipping(event) {
    event.preventDefault();
    const seen = new Set();
    const rows = shippingRows.map((row) => ({ name: row.name.trim(), fee: Number(row.fee) }));
    const broken = rows.some((row) => {
      const key = row.name.toLocaleLowerCase();
      if (!row.name || !Number.isFinite(row.fee) || row.fee < 0 || seen.has(key)) return true;
      seen.add(key);
      return false;
    });
    if (!rows.length || broken) {
      setShippingError('Every row needs a governorate name and a fee, and names cannot repeat.');
      return;
    }

    setShippingBusy(true);
    setShippingError('');
    try {
      const response = await authFetch('/api/shipping', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fees: rows }),
      });
      if (!response.ok) throw new Error(await serverError(response, 'The server could not save the shipping fees.'));
      const data = await response.json();
      setShippingRows(toShippingRows(data.fees ?? {}));
      setShippingMessage('Shipping fees saved.');
    } catch (error) {
      setShippingError(describeFailure(error, 'Could not reach the server. Run `npm run server`, then retry.'));
    } finally {
      setShippingBusy(false);
    }
  }

  function editPayment(event) {
    const { id, value } = event.target;
    setPaymentMessage('');
    setPaymentError('');
    setPaymentForm((form) => ({ ...form, [id]: value }));
  }

  async function submitPayment(event) {
    event.preventDefault();
    setPaymentBusy(true);
    setPaymentError('');
    try {
      const response = await authFetch('/api/payment', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ payment: paymentForm }),
      });
      if (!response.ok) throw new Error(await serverError(response, 'The server could not save the payment numbers.'));
      const data = await response.json();
      setPaymentForm({ ...EMPTY_PAYMENT, ...(data.payment ?? {}) });
      setPaymentMessage('Payment numbers saved.');
      reloadPayment();
    } catch (error) {
      setPaymentError(describeFailure(error, 'Could not reach the server. Run `npm run server`, then retry.'));
    } finally {
      setPaymentBusy(false);
    }
  }

  function pickPhoto(event) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (photo?.url) URL.revokeObjectURL(photo.url);
    setPhoto({ file, url: URL.createObjectURL(file) });
  }

  function clearPhoto() {
    if (photo?.url) URL.revokeObjectURL(photo.url);
    setPhoto(null);
    setProductForm((form) => ({ ...form, primaryColor: '', primaryColorName: '' }));
    if (photoInput.current) photoInput.current.value = '';
  }

  function reportSaved(message) {
    setProductError('');
    setProductMessage(message);
    reload();
  }

  function reportFailed(message) {
    setProductMessage('');
    setProductError(message);
  }

  async function submitProduct(event) {
    event.preventDefault();
    setProductError('');
    setProductMessage('');

    if (photo?.file && (!productForm.primaryColor || !productForm.primaryColorName.trim())) {
      setProductError('Choose the main photo color and enter its name.');
      return;
    }
    if (productVariantDrafts.some((variant) => !variant.color || !variant.name.trim() || !variant.file)) {
      setProductError('Complete each extra color with its color, name and photo.');
      return;
    }
    const creatingCategory = productForm.category === NEW_CATEGORY;
    if (creatingCategory && !newCategory.trim()) {
      setProductError('Write the new category name first.');
      return;
    }

    setProductBusy(true);
    try {
      let categoryId = productForm.category;
      if (creatingCategory) {
        const created = await authFetch('/api/categories', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: newCategory.trim() }),
        });
        if (!created.ok) throw new Error(await serverError(created, 'The server refused the new category.'));
        categoryId = (await created.json()).category?.id ?? categoryId;
      }

      const data = new FormData();
      data.append(
        'product',
        JSON.stringify({
          name: productForm.name,
          category: categoryId,
          price: Number(productForm.price),
          oldPrice: Number(productForm.oldPrice) || undefined,
          sizes: toSizeList(productForm.sizes),
          primaryColor: photo?.file ? productForm.primaryColor : '',
          primaryColorName: photo?.file ? productForm.primaryColorName.trim() : '',
          colorVariants: productVariantDrafts.map(({ color, name }, imageIndex) => ({ color, name, imageIndex })),
          gallery: productGalleryDrafts.map(({ image }, imageIndex) => ({ image, imageIndex })),
          tag: productForm.tag || undefined,
        })
      );
      if (photo?.file) data.append('image', photo.file, photo.file.name);
      productVariantDrafts.forEach((variant, index) => {
        if (variant.file) data.append(`variantImage-${index}`, variant.file, variant.file.name);
      });
      productGalleryDrafts.forEach((shot, index) => {
        if (shot.file) data.append(`galleryImage-${index}`, shot.file, shot.file.name);
      });

      const response = await authFetch('/api/products', { method: 'POST', body: data });
      if (!response.ok) throw new Error(await serverError(response, 'The server refused the product.'));
      setProductMessage(`"${productForm.name.trim()}" is in the lineup now.`);
      setProductForm({ ...EMPTY_PRODUCT, category: categoryId });
      setNewCategory('');
      productVariantDrafts.forEach((variant) => {
        if (variant.preview?.startsWith('blob:')) URL.revokeObjectURL(variant.preview);
      });
      setProductVariantDrafts([]);
      productGalleryDrafts.forEach((shot) => {
        if (shot.preview?.startsWith('blob:')) URL.revokeObjectURL(shot.preview);
      });
      setProductGalleryDrafts([]);
      if (photo?.url) URL.revokeObjectURL(photo.url);
      setPhoto(null);
      if (photoInput.current) photoInput.current.value = '';
      reload();
    } catch (error) {
      setProductError(describeFailure(error, 'Could not save the product. Run `npm run server` and try again.'));
    } finally {
      setProductBusy(false);
    }
  }

  async function submitReview(event) {
    event.preventDefault();
    setReviewError('');
    setReviewMessage('');

    setReviewBusy(true);
    try {
      const response = await authFetch('/api/reviews', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: reviewForm.reviewName, city: reviewForm.city, text: reviewForm.text }),
      });
      if (!response.ok) throw new Error(await serverError(response, 'The server refused the review.'));
      setReviewMessage(`"${reviewForm.reviewName.trim()}" is on the reviews strip now.`);
      setReviewForm(EMPTY_REVIEW);
      reload();
    } catch (error) {
      setReviewError(describeFailure(error, 'Could not save the review. Run `npm run server` and try again.'));
    } finally {
      setReviewBusy(false);
    }
  }

  async function deleteReview(review) {
    if (!window.confirm(`Remove the review from "${review.name}"?`)) return;
    setReviewError('');
    setReviewMessage('');
    setReviewDeletingId(review.id);
    try {
      const response = await authFetch(`/api/reviews/${encodeURIComponent(review.id)}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await serverError(response, 'The server refused the delete.'));
      setReviewMessage(`The review from "${review.name}" was removed.`);
      reload();
    } catch (error) {
      setReviewError(describeFailure(error, 'Could not delete the review. Run `npm run server` and try again.'));
    } finally {
      setReviewDeletingId('');
    }
  }

  if (!authenticated) {
    return (
      <main className="admin" dir="ltr">
        <div className="admin__locked">
          <h1 className="admin__title">Admin</h1>
          {sessionStatus === 'checking' ? (
            <p className="admin__notice">Checking your sign in&hellip;</p>
          ) : (
            <>
              <p className="admin__lead">
                This page is for business owners only.
              </p>
              <LoginForm />
            </>
          )}
        </div>
      </main>
    );
  }

  return (
    <main className="admin" dir="ltr">
      <header className="admin__head">
        <div>
          <h1 className="admin__title">Admin</h1>
          <p className="admin__lead">
            Signed in as <strong className="admin__who">{adminName}</strong> &mdash; manage incoming orders, order history,
            products, and reviews.
          </p>
        </div>
        <div className="admin__head-actions">
          <button type="button" className="admin__ghost" onClick={refreshAll}>
            Refresh
          </button>
          <button type="button" className="admin__ghost" onClick={logout}>
            Log out
          </button>
        </div>
      </header>

      {load.code === 'bad-admin-username' && (
        <p className="admin__notice">The admin username saved on this server breaks the rules.</p>
      )}

      <div className="admin__nav" role="tablist" aria-label="Admin sections">
        {SECTIONS.map((item) => (
          <button
            type="button"
            key={item.id}
            role="tab"
            id={`admin-tab-${item.id}`}
            aria-selected={section === item.id}
            aria-controls={`admin-panel-${item.id}`}
            className={`admin__nav-link${section === item.id ? ' admin__nav-link--active' : ''}`}
            onClick={() => setSection(item.id)}
          >
            {item.label}
            {item.id === 'orders' && orders ? ` (${pendingOrders.length})` : ''}
            {item.id === 'history' && orders ? ` (${reviewedOrders.length})` : ''}
            {item.id === 'products' ? ` (${products.length})` : ''}
            {item.id === 'reviews' ? ` (${reviews.length})` : ''}
          </button>
        ))}
      </div>

      {section === 'orders' && (
        <section className="admin__section" id="admin-panel-orders" role="tabpanel" aria-labelledby="admin-tab-orders">
          <h2 className="admin__section-title">Orders {orders ? `(${pendingOrders.length})` : ''}</h2>

          {ordersError && <p className="admin__notice">{ordersError}</p>}
          {!orders && !ordersError && <p className="admin__notice">Loading orders&hellip;</p>}
          {orders !== null && !ordersError && allOrders.length === 0 && (
            <p className="admin__notice">No order has been placed yet.</p>
          )}
          {orders !== null && !ordersError && allOrders.length > 0 && pendingOrders.length === 0 && (
            <p className="admin__notice">All orders have been reviewed. See them in Order History.</p>
          )}

          <ul className="admin__list">
            {pendingOrders.map((order) => (
              <li key={order.id}>
                <OrderCard order={order} onReview={toggleOrderReview} reviewBusy={reviewingOrderId === order.id} onDelete={deleteOrder} deleteBusy={deletingOrderId === order.id} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {section === 'history' && (
        <section className="admin__section" id="admin-panel-history" role="tabpanel" aria-labelledby="admin-tab-history">
          <h2 className="admin__section-title">Order History</h2>
          {ordersError && <p className="admin__notice">{ordersError}</p>}
          {!orders && !ordersError && <p className="admin__notice">Loading order history&hellip;</p>}
          {orders && !ordersError && (
            <>
              <div className="order-history__stats">
                <article className="order-history__stat">
                  <span>Total orders</span>
                  <strong>{allOrders.length}</strong>
                </article>
                <article className="order-history__stat">
                  <span>Reviewed orders</span>
                  <strong>{reviewedOrders.length}</strong>
                </article>
              </div>
              {reviewedOrders.length === 0 ? (
                <p className="admin__notice">Reviewed orders will appear here.</p>
              ) : (
                <ul className="admin__list">
                  {reviewedOrders.map((order) => (
                    <li key={order.id}>
                      <OrderCard order={order} onReview={toggleOrderReview} reviewBusy={reviewingOrderId === order.id} onDelete={deleteOrder} deleteBusy={deletingOrderId === order.id} />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>
      )}

      {section === 'products' && (
        <section
          className="admin__section"
          id="admin-panel-products"
          role="tabpanel"
          aria-labelledby="admin-tab-products"
        >
          <h2 className="admin__section-title">Products ({products.length})</h2>

          {productError && <p className="admin__notice">{productError}</p>}
          {productMessage && <p className="admin__notice admin__notice--done">{productMessage}</p>}

          <h3 className="admin__sub-title">Edit what is live</h3>
          <input
            className="admin__input product-search"
            type="search"
            value={productSearch}
            onChange={(event) => setProductSearch(event.target.value)}
            placeholder="Search products by name, category, or id"
            aria-label="Search products by name, category, or id"
          />
          <ul className="catalog">
            {filteredProducts.map((product) => (
              <ProductRow
                key={product.id}
                product={product}
                categories={categories}
                onDone={reportSaved}
                onFail={reportFailed}
              />
            ))}
          </ul>
          {filteredProducts.length === 0 && <p className="admin__notice">No products match your search.</p>}

        </section>
      )}

      {section === 'add-product' && (
        <section className="admin__section" id="admin-panel-add-product" role="tabpanel" aria-labelledby="admin-tab-add-product">
          <h2 className="admin__section-title">Add a new product</h2>
          {productError && <p className="admin__notice">{productError}</p>}
          {productMessage && <p className="admin__notice admin__notice--done">{productMessage}</p>}
          <form className="admin__form" onSubmit={submitProduct}>
            <Field id="name" label="Name" full>
              <input
                className="admin__input"
                id="name"
                value={productForm.name}
                onChange={editProduct}
                placeholder="Heavyweight Oversized Tee Black"
                required
              />
            </Field>

            <Field id="category" label="Category">
              <select className="admin__input" id="category" value={productForm.category} onChange={editProduct}>
                {categories.map((category) => (
                  <option value={category.id} key={category.id}>
                    {category.title}
                  </option>
                ))}
                <option value={NEW_CATEGORY}>New category</option>
              </select>
            </Field>

            {productForm.category === NEW_CATEGORY && (
              <Field id="newCategory" label="New category name">
                <input
                  className="admin__input"
                  id="newCategory"
                  value={newCategory}
                  onChange={(event) => setNewCategory(event.target.value)}
                  maxLength="40"
                  placeholder="Hoodies"
                />
              </Field>
            )}

            <Field id="price" label="Price (EGP)">
              <input
                className="admin__input"
                id="price"
                type="number"
                min="1"
                value={productForm.price}
                onChange={editProduct}
                required
              />
            </Field>

            <Field id="oldPrice" label="Was (EGP)" optional="optional">
              <input
                className="admin__input"
                id="oldPrice"
                type="number"
                min="0"
                value={productForm.oldPrice}
                onChange={editProduct}
              />
            </Field>

            <Field id="sizes" label="Sizes" full optional="optional">
              <ApparelSizePicker
                value={productForm.sizes}
                onChange={(sizes) => setProductForm((form) => ({ ...form, sizes }))}
              />
            </Field>

            <Field id="tag" label="Tag" optional="new / best, or empty">
              <input className="admin__input" id="tag" value={productForm.tag} onChange={editProduct} />
            </Field>

            <Field id="image" label="Photo" optional="jpg or png" full>
              <div className="admin-upload">
                <label className="admin-upload__box" htmlFor="image">
                  {photo ? (
                    <span className="admin-upload__filled">
                      <img className="admin-upload__img" src={photo.url} alt="" />
                      <span className="admin-upload__name">{photo.file.name}</span>
                    </span>
                  ) : (
                    'Tap to pick the product photo'
                  )}
                </label>
                {photo && (
                  <button type="button" className="admin-upload__clear" onClick={clearPhoto} aria-label="Remove the photo">
                    &times;
                  </button>
                )}
                <input
                  className="admin-upload__input"
                  ref={photoInput}
                  id="image"
                  type="file"
                  accept="image/*"
                  onChange={pickPhoto}
                />
              </div>
            </Field>

            {(
              <>
                <Field id="primaryColor" label="Main photo color" full optional={photo?.file ? 'required for this photo' : 'select when you upload the main photo'}>
                  <ColorChoice value={productForm.primaryColor} onChange={(value) => setProductForm((form) => ({ ...form, primaryColor: value }))} required={Boolean(photo?.file)} disabled={!photo?.file} placeholder="Choose the main photo color" ariaLabel="Main photo color" />
                </Field>
                <Field id="primaryColorName" label="Main photo color name" full optional={photo?.file ? 'required for this photo' : 'select when you upload the main photo'}>
                  <input
                    className="admin__input"
                    id="primaryColorName"
                    value={productForm.primaryColorName}
                    onChange={editProduct}
                    placeholder="e.g. Triple Black or White/Red"
                    required={Boolean(photo?.file)}
                    disabled={!photo?.file}
                  />
                </Field>
              </>
            )}

            {(
              <Field id="colorVariants" label="Other color photos" full optional="Each one has its own photo, color and name">
                <ProductVariantEditor
                  idPrefix="new-product-variant"
                  variants={productVariantDrafts}
                  onChange={setProductVariantDrafts}
                />
              </Field>
            )}

            <Field id="gallery" label="Detail photos" full optional="optional">
              <GalleryEditor
                idPrefix="new-product-gallery"
                photos={productGalleryDrafts}
                onChange={setProductGalleryDrafts}
              />
            </Field>

            <div className="admin__actions">
              <button type="submit" className="btn" disabled={productBusy}>
                {productBusy ? 'Saving' : 'Add product'}
              </button>
            </div>
          </form>
        </section>
      )}

      {section === 'reviews' && (
        <section
          className="admin__section"
          id="admin-panel-reviews"
          role="tabpanel"
          aria-labelledby="admin-tab-reviews"
        >
          <h2 className="admin__section-title">Reviews ({reviews.length})</h2>

          {reviewError && <p className="admin__notice">{reviewError}</p>}
          {reviewMessage && <p className="admin__notice admin__notice--done">{reviewMessage}</p>}

          <h3 className="admin__sub-title">On the strip now</h3>
          <ul className="catalog">
            {reviews.map((review, index) => (
              <li className="catalog__row" key={review.id ?? `${review.name}-${index}`}>
                <span className="catalog__name">{review.name}</span>
                <span className="catalog__meta">{review.city}</span>
                <span className="catalog__text">{review.text}</span>
                <button
                  type="button"
                  className="catalog__delete"
                  disabled={Boolean(reviewDeletingId)}
                  onClick={() => deleteReview(review)}
                >
                  {reviewDeletingId === review.id ? 'Removing…' : 'Remove'}
                </button>
              </li>
            ))}
          </ul>

        </section>
      )}

      {section === 'add-review' && (
        <section className="admin__section" id="admin-panel-add-review" role="tabpanel" aria-labelledby="admin-tab-add-review">
          <h2 className="admin__section-title">Add a new review</h2>
          {reviewError && <p className="admin__notice">{reviewError}</p>}
          {reviewMessage && <p className="admin__notice admin__notice--done">{reviewMessage}</p>}
          <form className="admin__form" onSubmit={submitReview}>
            <Field id="reviewName" label="Buyer name" full>
              <input
                className="admin__input"
                id="reviewName"
                value={reviewForm.reviewName}
                onChange={editReview}
                placeholder="Karim A."
                required
              />
            </Field>

            <Field id="city" label="City">
              <input className="admin__input" id="city" value={reviewForm.city} onChange={editReview} placeholder="Cairo" />
            </Field>

            <Field id="text" label="Review" full>
              <textarea
                className="admin__input admin__input--area"
                id="text"
                value={reviewForm.text}
                onChange={editReview}
                rows="4"
                placeholder="What did the buyer say?"
                required
              />
            </Field>

            <div className="admin__actions">
              <button type="submit" className="btn" disabled={reviewBusy}>
                {reviewBusy ? 'Saving' : 'Add review'}
              </button>
            </div>
          </form>
        </section>
      )}

      {section === 'shipping' && (
        <section className="admin__section" id="admin-panel-shipping" role="tabpanel" aria-labelledby="admin-tab-shipping">
          <h2 className="admin__section-title">Shipping fees</h2>

          {shippingError && <p className="admin__notice">{shippingError}</p>}
          {shippingMessage && <p className="admin__notice admin__notice--done">{shippingMessage}</p>}

          <form className="admin__form" onSubmit={submitShipping}>
            <div className="shipping-rows">
              {shippingRows.map((row, index) => (
                <div className="shipping-row" key={row.key}>
                  <input
                    className="admin__input"
                    value={row.name}
                    onChange={(event) => editShippingRow(index, 'name', event.target.value)}
                    placeholder="Governorate"
                    aria-label={`Governorate ${index + 1}`}
                  />
                  <input
                    className="admin__input"
                    type="number"
                    min="0"
                    step="1"
                    value={row.fee}
                    onChange={(event) => editShippingRow(index, 'fee', event.target.value)}
                    placeholder="0"
                    aria-label={`Delivery fee for ${row.name.trim() || `governorate ${index + 1}`}`}
                  />
                  <button type="button" className="catalog__delete" onClick={() => removeShippingRow(index)}>
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="admin__actions">
              <button type="button" className="admin__ghost" onClick={addShippingRow}>
                Add governorate
              </button>
              <button type="submit" className="btn" disabled={shippingBusy}>
                {shippingBusy ? 'Saving' : 'Save shipping fees'}
              </button>
            </div>
          </form>
        </section>
      )}

      {section === 'payment' && (
        <section className="admin__section" id="admin-panel-payment" role="tabpanel" aria-labelledby="admin-tab-payment">
          <h2 className="admin__section-title">Payment numbers</h2>

          {paymentError && <p className="admin__notice">{paymentError}</p>}
          {paymentMessage && <p className="admin__notice admin__notice--done">{paymentMessage}</p>}

          <form className="admin__form" onSubmit={submitPayment}>
            <div className="admin-field">
              <label className="admin-field__label" htmlFor="whatsapp">
                WhatsApp number
              </label>
              <input
                className="admin__input"
                id="whatsapp"
                type="tel"
                inputMode="tel"
                value={paymentForm.whatsapp}
                onChange={editPayment}
              />
            </div>

            {TRANSFER_METHODS.map((method) => (
              <div className="admin-field" key={method.id}>
                <label className="admin-field__label" htmlFor={method.id}>
                  {`${method.label} number`}
                </label>
                <input
                  className="admin__input"
                  id={method.id}
                  value={paymentForm[method.id]}
                  onChange={editPayment}
                />
              </div>
            ))}

            <div className="admin__actions">
              <button type="submit" className="btn" disabled={paymentBusy}>
                {paymentBusy ? 'Saving' : 'Save payment numbers'}
              </button>
            </div>
          </form>
        </section>
      )}
    </main>
  );
}

export default Admin;
